/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { Store as zStore } from '@jane/shared/types';

import type { StoreSchedule } from './storeSchedule';
import { buildStoreSchedule } from './storeSchedule';

export type ScheduleType = 'delivery' | 'pickup' | 'curbside' | 'retail';

type AnyStore = Store | zStore;
export type StoreWithSchedules = Pick<
  AnyStore,
  | 'delivery_hours'
  | 'pickup_hours'
  | 'curbside_hours'
  | 'working_hours'
  | 'time_zone_identifier'
>;

interface OrderingScheduleProps {
  store: StoreWithSchedules;
  type: ScheduleType[];
}

export interface OrderingSchedule {
  curbsideSchedule: StoreSchedule | null;
  deliverySchedule: StoreSchedule | null;
  pickupSchedule: StoreSchedule | null;
  retailSchedule: StoreSchedule | null;
}

/**
 * Get the ordering schedule for a store based on the type of schedule
 *
 * Accepts a store object and an array of types to determine which schedules to build.
 */

export const getOrderingSchedule = ({
  store,
  type,
}: OrderingScheduleProps): OrderingSchedule => {
  let deliverySchedule = null;
  let pickupSchedule = null;
  let curbsideSchedule = null;
  let retailSchedule = null;
  const timeZoneIdentifier = store?.time_zone_identifier;

  if (type.includes('delivery') && store?.delivery_hours) {
    deliverySchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'delivery',
      workingHours: [...store.delivery_hours],
    });
  }

  if (type.includes('pickup') && store?.pickup_hours) {
    pickupSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'pickup',
      workingHours: [...store.pickup_hours],
    });
  }

  if (type.includes('curbside') && store?.curbside_hours) {
    curbsideSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'curbside',
      workingHours: [...store.curbside_hours],
    });
  }

  if (type.includes('retail') && store?.working_hours) {
    retailSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'retail',
      workingHours: [...store.working_hours],
    });
  }

  return {
    curbsideSchedule,
    deliverySchedule,
    pickupSchedule,
    retailSchedule,
  };
};

export const isCurbsideScheduleOpen = (store: StoreWithSchedules): boolean => {
  const { curbsideSchedule } = getOrderingSchedule({
    store,
    type: ['curbside'],
  });
  return curbsideSchedule ? !curbsideSchedule.isAlwaysClosed() : false;
};
